import React from "react";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

import QuizControls from "./components/QuizControls";
import QuizEmptyState from "./components/QuizEmptyState";
import QuizHero from "./components/QuizHero";
import QuizQuestion from "./components/QuizQuestion";
import QuizResults from "./components/QuizResults";
import QuizSkeleton from "./components/QuizSkeleton";
import { useTechniquesQuizController } from "./hooks/useTechniquesQuizController";
import { MdQuiz } from "react-icons/md";

const GRADES = ["Jaune", "Orange", "Verte", "Bleue", "Marron", "Noire"];

export default function TechniquesQuizz() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const {
    loading,
    error,
    selectedGrades,
    gradeLabel,
    toggleGrade,
    clearSelectedGrades,
    includeSelfDefense,
    toggleSelfDefense,
    questions,
    current,
    selected,
    score,
    errors,
    finished,
    progress,
    currentQuestion,
    availableTechniquesCount,
    onAnswer,
    nextQuestion,
    restart,
    refreshTechniques,
  } = useTechniquesQuizController({ questionCount: 10 });

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Quizz Techniques"
          subtitle="Faites un quizz pour tester vos connaissances en techniques de judo."
          icon={MdQuiz}
          align="left"
          className="mb-4"
        />

        <div className="mb-5 sm:mb-7">
          <DashboardNavigation role={userRole} />
        </div>

        <QuizHero
          score={score}
          current={current}
          total={questions.length}
          finished={finished}
          loading={loading}
          availableTechniquesCount={availableTechniquesCount}
          includeSelfDefense={includeSelfDefense}
          grade={gradeLabel}
        />

        <div className="mt-4 overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-xl shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:shadow-black/20 sm:mt-6">
          <div className="border-b border-slate-200/70 bg-white/70 px-3 py-3 dark:border-slate-800 dark:bg-slate-900/60 sm:px-5">
            <QuizControls
              grades={GRADES}
              selectedGrades={selectedGrades}
              onToggleGrade={toggleGrade}
              onClearGrades={clearSelectedGrades}
              includeSelfDefense={includeSelfDefense}
              onToggleSelfDefense={toggleSelfDefense}
              disabled={loading}
              finished={finished}
              onRestart={restart}
              onRefresh={refreshTechniques}
            />
          </div>

          <div className="p-3 sm:p-5 lg:p-6">
            {loading && <QuizSkeleton />}

            {!loading && error && (
              <QuizEmptyState
                variant="error"
                title="Impossible de charger les techniques"
                description={error}
                actionLabel="Réessayer"
                onAction={refreshTechniques}
              />
            )}

            {!loading && !error && !finished && questions.length > 0 && (
              <QuizQuestion
                question={currentQuestion}
                selected={selected}
                onAnswer={onAnswer}
                onNext={nextQuestion}
                hasNext={current < questions.length - 1}
                current={current}
                total={questions.length}
                score={score}
                progress={progress}
              />
            )}

            {!loading && !error && finished && (
              <QuizResults
                questions={questions}
                errors={errors}
                score={score}
                onRestart={restart}
              />
            )}

            {!loading && !error && !finished && questions.length === 0 && (
              <QuizEmptyState
                title="Pas assez de techniques"
                description="Impossible de générer un quizz avec ces critères. Essaie une autre ceinture ou active la Self-Défense."
                actionLabel="Recommencer avec toutes les ceintures"
                onAction={clearSelectedGrades}
              />
            )}
          </div>
        </div>
      </main>
    </section>
  );
}
